import { useState, useCallback } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import InnerPageHero from '@/components/feature/InnerPageHero';
import { useEnquiryModal } from '@/hooks/useEnquiryModal';
import FadeInSection from '@/components/base/FadeInSection';

interface Certificate {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  color: string;
  pages: string[];
}

const certificates: Certificate[] = [
  {
    id: 'fssai',
    title: 'FSSAI Registration',
    subtitle: 'Food Safety & Standards Authority of India',
    description:
      'Bassi Enterprises is registered under FSSAI, ensuring compliance with food safety standards and quality requirements for fresh produce supply. Registration Number: 22726396000236.',
    color: 'primary',
    pages: [
      '/images/FSSI_license_page-0001.jpg',
      '/images/FSSI_license_page-0002.jpg',
      '/images/FSSI_license_page-0003.jpg',
      '/images/FSSI_license_page-0004.jpg',
      '/images/FSSI_license_page-0005.jpg',
    ],
  },
  {
    id: 'msme',
    title: 'MSME Registration',
    subtitle: 'Ministry of Micro, Small & Medium Enterprises',
    description:
      'Bassi Enterprises is officially registered as a Micro Enterprise under Udyam Registration. Udyam Number: UDYAM-UP-44-0039003. Major Activity: Trading.',
    color: 'accent',
    pages: [
      '/images/UdyamRegistrationCertificate_page-0001.jpg',
      '/images/UdyamRegistrationCertificate_page-0002.jpg',
    ],
  },
  {
    id: 'gst',
    title: 'GST Registration',
    subtitle: 'Goods & Services Tax',
    description:
      'Bassi Enterprises is GST registered, enabling transparent business transactions and professional B2B supply partnerships. Registration Number: 09ESWPB5086D1ZB.',
    color: 'secondary',
    pages: [
      '/images/gst_basharat_page-0001.jpg',
      '/images/gst_basharat_page-0002.jpg',
      '/images/gst_basharat_page-0003.jpg',
    ],
  },
];

const colorMap: Record<string, { border: string; ring: string }> = {
  primary: {
    border: 'border-primary-200',
    ring: 'ring-primary-300',
  },
  accent: {
    border: 'border-primary-200',
    ring: 'ring-primary-300',
  },
  secondary: {
    border: 'border-primary-200',
    ring: 'ring-primary-300',
  },
};

function CertificateLightbox({
  cert,
  onClose,
}: {
  cert: Certificate;
  onClose: () => void;
}) {
  const [pageIndex, setPageIndex] = useState(0);
  const c = colorMap[cert.color];

  const nextPage = useCallback(() => {
    setPageIndex((prev) => (prev + 1) % cert.pages.length);
  }, [cert.pages.length]);

  const prevPage = useCallback(() => {
    setPageIndex((prev) => (prev - 1 + cert.pages.length) % cert.pages.length);
  }, [cert.pages.length]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 md:p-8"
      onClick={onClose}
    >
      <button
        className="absolute top-4 right-4 z-50 w-10 h-10 flex items-center justify-center rounded-full bg-foreground-900/60 text-background-50 hover:bg-foreground-900/80 transition-colors cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label="Close"
      >
        <i className="ri-close-line text-xl"></i>
      </button>

      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 px-4 py-1.5 rounded-full bg-foreground-900/60 text-background-50 text-sm font-medium">
        Page {pageIndex + 1} of {cert.pages.length}
      </div>

      <div
        className="relative max-w-3xl w-full max-h-[85vh] flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={cert.pages[pageIndex]}
          alt={`${cert.title} - Page ${pageIndex + 1}`}
          className="max-h-[75vh] w-auto object-contain rounded-lg shadow-2xl"
        />

        {cert.pages.length > 1 && (
          <>
            <button
              className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-foreground-900/50 text-background-50 hover:bg-foreground-900/80 transition-colors cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                prevPage();
              }}
              aria-label="Previous page"
            >
              <i className="ri-arrow-left-s-line text-2xl"></i>
            </button>
            <button
              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-foreground-900/50 text-background-50 hover:bg-foreground-900/80 transition-colors cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                nextPage();
              }}
              aria-label="Next page"
            >
              <i className="ri-arrow-right-s-line text-2xl"></i>
            </button>
          </>
        )}
      </div>

      {cert.pages.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2">
          {cert.pages.map((url, idx) => (
            <button
              key={url}
              className={`w-12 h-16 rounded-md overflow-hidden border-2 transition-all cursor-pointer ${
                idx === pageIndex
                  ? `border-white ${c.ring} ring-2`
                  : 'border-white/30 opacity-60 hover:opacity-100'
              }`}
              onClick={(e) => {
                e.stopPropagation();
                setPageIndex(idx);
              }}
              aria-label={`Go to page ${idx + 1}`}
            >
              <img
                src={url}
                alt={`Page ${idx + 1} thumbnail`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Certificates() {
  const [activeCert, setActiveCert] = useState<Certificate | null>(null);
  const { openEnquiry } = useEnquiryModal();

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <InnerPageHero
          bgImage="/images/certificates-hero.jpg"
          bgAlt="Bassi Enterprises certifications and registrations"
          bgTitle="Bassi Enterprises - Certificates & Business Registrations"
          breadcrumbLabel="Certificates"
        >
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-background-50/15 backdrop-blur-sm text-background-50 rounded-full text-xs font-semibold mb-5 border border-background-50/15">
              <i className="ri-verified-badge-fill"></i>
              Business Compliance
            </span>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-4 leading-tight">
              Certificates &amp; Business Registrations
            </h1>
            <p className="text-background-200 text-base md:text-lg leading-relaxed">
              Bassi Enterprises is a registered and compliant fresh fruits and vegetables supplier. Our official registrations demonstrate our commitment to transparency, quality standards, and professional business practices.
            </p>
          </div>
        </InnerPageHero>

        {/* Certificate Cards */}
        <section className="py-14 md:py-20 px-4 md:px-6 lg:px-10">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {certificates.map((cert, idx) => {
                const c = colorMap[cert.color];
                return (
                  <FadeInSection key={cert.id} delay={idx * 120}>
                  <div
                    className={`group bg-background-50 border ${c.border} rounded-2xl overflow-hidden hover:shadow-md transition-all duration-300`}
                  >
                      <div className="relative w-full bg-background-100 cursor-pointer overflow-hidden" onClick={() => setActiveCert(cert)}>
                        <div className="w-full flex items-center justify-center py-8 px-6">
                          <img
                            src={cert.pages[0]}
                            alt={cert.title}
                            className="max-w-full max-h-[420px] object-contain rounded-md shadow-sm hover:shadow-md transition-shadow duration-300"
                          />
                        </div>
                        <div className="absolute top-4 right-4">
                          <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-primary-500 text-background-50 rounded-full text-xs font-semibold whitespace-nowrap shadow-sm">
                            <i className="ri-verified-badge-fill text-xs"></i>
                            Registered & Compliant
                          </span>
                        </div>
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-foreground-900/60 text-background-50 rounded-full text-xs font-medium">
                            <i className="ri-zoom-in-line"></i>
                            Click to view all pages
                          </span>
                        </div>
                      </div>
                      <div className="p-6 md:p-7">
                        <span className="text-foreground-400 text-xs font-medium uppercase tracking-wider">
                          {cert.subtitle}
                        </span>
                        <h3 className="font-heading text-xl font-bold text-foreground-950 mt-1.5 mb-3">
                          {cert.title}
                        </h3>
                        <p className="text-foreground-500 text-sm leading-relaxed">
                          {cert.description}
                        </p>
                        <div className="mt-5 pt-5 border-t border-background-200 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <i className="ri-check-double-line text-primary-600 text-sm"></i>
                            <span className="text-foreground-600 text-xs">Verified Government Registration</span>
                          </div>
                          <button
                            onClick={() => setActiveCert(cert)}
                            className="text-primary-600 hover:text-primary-700 text-xs font-semibold inline-flex items-center gap-1 cursor-pointer"
                          >
                            View All Pages
                            <i className="ri-arrow-right-line"></i>
                          </button>
                        </div>
                      </div>
                  </div>
                  </FadeInSection>
                );
              })}
            </div>
          </div>
        </section>

        {/* Why Our Registrations Matter */}
        <section className="py-14 md:py-20 px-4 md:px-6 lg:px-10 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <FadeInSection>
              <div className="text-center mb-10 md:mb-14">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-100 text-primary-700 rounded-full text-xs font-semibold mb-4">
                  <i className="ri-shield-star-line"></i>
                  Trust & Compliance
                </span>
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
                  Why Our Registrations Matter
                </h2>
                <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
                  Registered business identity. Our certifications show that Bassi Enterprises
                  follows proper compliance practices and maintains transparency for our business partners.
                </p>
              </div>
            </FadeInSection>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {[
                {
                  icon: 'ri-store-3-line',
                  title: 'Quick Commerce Companies',
                  desc: 'Zomato, Blinkit, Zepto and other platforms can partner with a fully verified supplier.',
                },
                {
                  icon: 'ri-restaurant-line',
                  title: 'Restaurants',
                  desc: 'FSSAI registration ensures our produce meets food safety standards required by restaurants.',
                },
                {
                  icon: 'ri-hotel-line',
                  title: 'Hotels',
                  desc: 'GST registration enables transparent invoicing and professional supply chain for hotel operations.',
                },
                {
                  icon: 'ri-shopping-cart-line',
                  title: 'Retail Chains',
                  desc: 'MSME registration supports our credibility as a reliable long-term supply partner.',
                },
                {
                  icon: 'ri-truck-line',
                  title: 'Distributors',
                  desc: 'Proper registrations ensure smooth B2B transactions and compliance with distribution networks.',
                },
                {
                  icon: 'ri-building-line',
                  title: 'Corporate Buyers',
                  desc: 'Verified documentation builds confidence for corporate procurement and bulk supply agreements.',
                },
              ].map((item, idx) => (
                <FadeInSection key={item.title} delay={idx * 80}>
                <div
                  className="bg-background-50 rounded-xl p-5 md:p-6 border border-background-200 hover:border-primary-200 transition-all duration-300"
                >
                    <div className="w-11 h-11 rounded-lg bg-primary-100 flex items-center justify-center mb-4">
                      <i className={`${item.icon} text-primary-600 text-lg`}></i>
                    </div>
                    <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-2">
                      {item.title}
                    </h3>
                    <p className="text-foreground-500 text-xs leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </FadeInSection>
              ))}
            </div>
          </div>
        </section>

        {/* Partner With A Registered Supplier CTA */}
        <section className="py-14 md:py-20 px-4 md:px-6 lg:px-10">
          <FadeInSection>
            <div className="max-w-4xl mx-auto bg-primary-50 rounded-3xl p-8 md:p-12 text-center border border-primary-200">
              <div className="w-14 h-14 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-background-50 text-2xl"></i>
              </div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Partner With A Registered Supplier
              </h2>
              <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto leading-relaxed mb-8">
                Looking for a reliable fresh fruits and vegetables supplier?
                Work with a verified business partner with proper registrations and compliance.
              </p>
              <button
                onClick={() => openEnquiry('Fresh Fruits & Vegetables Bulk Supply')}
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-full text-base transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-fill text-xl"></i>
                Request Bulk Supply Quote
              </button>
            </div>
          </FadeInSection>
        </section>
      </main>
      <Footer />

      {activeCert && (
        <CertificateLightbox
          cert={activeCert}
          onClose={() => setActiveCert(null)}
        />
      )}
    </div>
  );
}